import React from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Button,
  Card,
  createTheme,
  ProgressBar,
  ThemeProvider,
  Typography,
} from '@one-thd/sui-atomic-components';
import { ArrowForward } from '@one-thd/sui-icons';
import {
  windowExists,
  isAuthenticated,
  gotoLogonModule,
  handleSignOutSignIn,
} from '../../../../common/utils/AccountUtils';
import { useImageError } from '../../utils/SweepstakesUtils';
import { useSweepstakesData } from '../../../../contexts/SweepstakesDataContext';
import { SWEEPSTAKES_MOCK_DATA } from '../../../../core/Data/SweepstakesMockData';
import { SWEEPSTAKES_CONSTANTS } from '../../../../core/Constants';
import { useResponsive } from '../../../../hooks';
import { mapSweepstakesData } from '../../utils/SweepstakesDataMapper';
import { SweepstakesMarkdown } from '../../utils/SweepstakesMarkdown';
import {
  RenderGenericError,
  RenderSweepstakesCardLoading,
} from '../SweepstakesUtils';

// Custom theme for the sweepstakes progress bar
// Brand orange (#F96302) for in-progress, light green (#9CE8C4) for complete
const progressBarTheme = createTheme({
  palette: {
    background: {
      'warning-strong': '249 99 2',
      'success-strongest': '156 232 196'
    }
  }
});

// Utility functions
const getHeaderClasses = (showOutsideHeaderOn, showInsideHeaderOn) => {
  const buildClasses = (breakpoints) => {
    if (!breakpoints || (Array.isArray(breakpoints) && breakpoints.length === 0)) {
      return 'sui-hidden';
    }

    const bps = Array.isArray(breakpoints) ? breakpoints : [breakpoints];
    const hasMW = bps.includes('MW');
    const hasTW = bps.includes('TW');
    const hasDT = bps.includes('DT');

    const classes = [];

    // Set default mobile visibility
    if (hasMW) {
      classes.push('sui-block');
    } else {
      classes.push('sui-hidden');
    }

    // Set tablet visibility (sm breakpoint)
    if (hasTW) {
      classes.push('sm:sui-block');
    } else {
      classes.push('sm:sui-hidden');
    }

    // Set desktop visibility (lg breakpoint)
    if (hasDT) {
      classes.push('lg:sui-block');
    } else {
      classes.push('lg:sui-hidden');
    }

    return classes.join(' ');
  };

  return {
    outside: buildClasses(showOutsideHeaderOn),
    inside: buildClasses(showInsideHeaderOn),
  };
};
// Render components
const RenderLogo = ({ logoUrl, logoFallback, imgRetryCounter }) => {
  if (!logoUrl) return null;

  return logoFallback.hasError ? (
    <img
      src={SWEEPSTAKES_CONSTANTS.URLS.LOGO_FALLBACK}
      alt="Logo"
      className="sui-w-12 sui-flex-shrink-0"
      width="48"
      height="auto"
      data-testid="sweepstakes-logo-fallback"
    />
  ) : (
    <img
      key={logoUrl + imgRetryCounter}
      src={logoUrl}
      alt="Logo"
      className="sui-w-12 sui-flex-shrink-0"
      width="48"
      height="auto"
      onError={logoFallback.handleError}
      data-testid="sweepstakes-logo"
    />
  );
};

const RenderStatusBadge = ({ status }) => (
  <Badge color="success" variant="outline" data-testid="sweepstakes-status-badge">
    {isAuthenticated() ? status || 'New' : 'New'}
  </Badge>
);

const RenderTitle = ({ title }) => {
  if (!title) return null;

  return (
    <Typography variant="h6" weight="bold" height="snug" data-testid="sweepstakes-title">
      {title}
    </Typography>
  );
};

const RenderDescription = ({ description }) => {
  if (!description) return null;

  return (
    <div className="sui-mb-2" data-testid="sweepstakes-description">
      <SweepstakesMarkdown markdownText={description} disableSpacing />
    </div>
  );
};

const RenderSignInButton = ({ isAdmin, onClick }) => (
  <div className="sui-pt-2">
    <Button
      variant="primary"
      size="small"
      onClick={onClick}
      data-testid="sweepstakes-sign-in-button"
    >
      {SWEEPSTAKES_CONSTANTS.AUTH_CTA_TEXT(isAdmin)}
    </Button>
  </div>
);

const RenderGetStartedButton = ({ onClick, label }) => (
  <div className="sui-pt-2">
    <Button
      variant="primary"
      size="small"
      onClick={onClick}
      data-testid="sweepstakes-get-started-button"
    >
      {label || 'Get Started'}
    </Button>
  </div>
);

const RenderDetailsButton = ({ onClick }) => (
  <button
    type="button"
    className="sui-text-brand sui-p-2 sui-cursor-pointer sui-rounded-md sui-transition-all hover:sui-bg-gray-50"
    onClick={onClick}
    aria-label="View sweepstakes details"
    data-testid="sweepstakes-details-button"
  >
    <ArrowForward />
  </button>
);

const RenderProgressSection = ({ sweepstakesData }) => {
  if (!sweepstakesData?.isOptedIn) return null;

  const { completedSteps = 0, totalSteps = 0 } = sweepstakesData;
  const percentComplete = totalSteps === 0
    ? 0
    : Math.round((completedSteps / totalSteps) * 100);
  const isComplete = percentComplete === 100;

  const btnText = isComplete
    ? sweepstakesData.completedCta
    : sweepstakesData.inProgressCta;
  const progressStatus = isComplete ? 'success' : 'warning';
  const labelText = isComplete
    ? (sweepstakesData.messageComplete || '').replace(/\*/g, '')
    : `${completedSteps} of ${totalSteps} Earned`;

  const handleOpenDrawer = () => {
    if (!windowExists()) return;
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(
      'loyalty-benefits.open-sweepstakes-details-drawer',
      { sweepstakesData }
    );
  };

  return (
    <div data-testid="sweepstakes-progress-section">
      <div className="sui-p-3 sui-bg-primary sui-rounded-md">
        <ThemeProvider theme={progressBarTheme}>
          <ProgressBar
            disableStatusIcon
            status={progressStatus}
            startLabel={
              isComplete
                ? <Typography variant="body-base" weight="bold">{labelText}</Typography>
                : labelText
            }
            value={percentComplete}
            size="large"
          />
        </ThemeProvider>
      </div>
      <div className="sui-pt-2 sui-mt-2">
        <Button
          data-testid="sweepstakes-entries-button"
          variant="primary"
          size="small"
          onClick={handleOpenDrawer}
        >
          {btnText}
        </Button>
      </div>
    </div>
  );
};

const RenderHeaderLink = ({ href, openNewWindow }) => (
  <a
    href={href}
    target={openNewWindow ? '_blank' : '_self'}
    rel={openNewWindow ? 'noopener noreferrer' : undefined}
    className="sui-cursor-pointer sui-underline"
    data-testid="sweepstakes-faqs-link"
    aria-label="View sweepstakes frequently asked questions"
  >
    Sweeps FAQs
  </a>
);

const RenderHeaderButton = ({ onClick }) => (
  <button
    type="button"
    className="sui-cursor-pointer sui-underline"
    onClick={onClick}
    data-testid="sweepstakes-faqs-link"
    aria-label="View sweepstakes frequently asked questions (opens in new tab)"
  >
    Sweeps FAQs
  </button>
);

const RenderHeader = ({ href, onClick, openNewWindow }) => (
  <div className="sui-flex sui-items-center sui-justify-between sui-mb-4">
    <Typography variant="h5" data-testid="sweepstakes-header-title">
      Sweepstakes Available (1)
    </Typography>
    <div>
      {href && <RenderHeaderLink href={href} openNewWindow={openNewWindow} />}
      {!href && onClick && <RenderHeaderButton onClick={onClick} />}
    </div>
  </div>
);

const RenderErrorCard = ({ refresh }) => {
  const testIdStr = 'sweepstakes-entry-card-error';
  return (
    <Card grow>
      <RenderGenericError refresh={refresh} testId={testIdStr} />
    </Card>
  );
};

const RenderLoadingCard = () => (
  <Card grow data-testid="sweepstakes-entry-card">
    <RenderSweepstakesCardLoading />
  </Card>
);

const unauthenticatedData = () => {
  return (windowExists() && !isAuthenticated() ? mapSweepstakesData(SWEEPSTAKES_MOCK_DATA.unauthenticated) : null);
};

const purchaserData = (isAdmin) => {
  return (!isAdmin ? mapSweepstakesData(SWEEPSTAKES_MOCK_DATA.purchaser) : null);
};

const showMobileButton = ({ isMobile, isPreEntryStatus }) => {
  return SWEEPSTAKES_CONSTANTS.ENABLE_MOBILE_CTA
    && isMobile
    && isAuthenticated()
    && isPreEntryStatus;
};

export const SweepstakesEntryCard = ({ SweepstakesEntryCardHeaderProps }) => {
  const {
    sweepstakesData, isLoading, hasError, refresh, isAdmin
  } = useSweepstakesData();
  const { isMobile } = useResponsive();

  // For unauthenticated users, use dedicated unauthenticated mock data if no sweepstakesData is provided
  // sweepstakesData is already mapped in the fetch hook
  const displayData = sweepstakesData
    || unauthenticatedData()
    || purchaserData(isAdmin);

  // Handle logo fallback - use generic image error hook
  const logoFallback = useImageError();

  // Create useState counter and useEffect to increment once
  const [imgRetryCounter, setCounter] = React.useState(0);
  React.useEffect(() => {
    if (imgRetryCounter === 0) setCounter((prev) => prev + 1);
  }, [imgRetryCounter]);

  const handleAuthClick = !isAdmin ? handleSignOutSignIn : gotoLogonModule;

  const handleDetailsClick = React.useCallback((event) => {
    event.stopPropagation();
    if (isLoading || hasError || !windowExists() || !displayData) {
      return;
    }

    // Determine which drawer to open based on sweepstakes status
    if (displayData.isOptedIn) {
      const completedSteps = displayData.completedSteps || 0;

      // If the user is opted in but hasn't completed any steps yet,
      // the expected experience is to start the first step (survey).
      if (completedSteps === 0) {
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-survey-drawer', {
          sweepstakesData: displayData,
        });
        return;
      }

      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-details-drawer', {
        sweepstakesData: displayData,
      });
    } else {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-opt-in-drawer', {
        sweepstakesData: displayData,
      });
    }
  }, [isLoading, hasError, displayData]);

  // Mobile CTA card: show "Get Started" button instead of arrow + stepper
  // when activityStatus is ELIGIBLE, INVITED, or IN_PROGRESS
  // Toggle SWEEPSTAKES_CONSTANTS.ENABLE_MOBILE_CTA to false to revert to old experience
  const isPreEntryStatus = displayData?.activityStatus
    && SWEEPSTAKES_CONSTANTS.PRE_OPT_IN_ACTIVITY_STATUS.includes(displayData.activityStatus);
  const showMobileGetStarted = showMobileButton({ isMobile, isPreEntryStatus });
  const showSigninButton = !isAuthenticated() || !isAdmin;

  const headerClasses = SweepstakesEntryCardHeaderProps
    ? getHeaderClasses(
      SweepstakesEntryCardHeaderProps.showOutsideHeaderOn,
      SweepstakesEntryCardHeaderProps.showInsideHeaderOn
    )
    : null;

  if (isLoading) return <RenderLoadingCard />;
  if (isAuthenticated() && hasError) return <RenderErrorCard refresh={refresh} />;

  const hasSteps = displayData?.steps && displayData?.steps?.length > 0;
  const showGetStarted = !displayData?.isOptedIn;

  return (
    <div suppressHydrationWarning>
      {SweepstakesEntryCardHeaderProps?.showOutsideHeaderOn && (
        <div className={headerClasses.outside} data-testid="sweepstakes-header-outside">
          <RenderHeader
            href={SweepstakesEntryCardHeaderProps.href}
            onClick={SweepstakesEntryCardHeaderProps.onClick}
            openNewWindow={SweepstakesEntryCardHeaderProps.openNewWindow}
          />
        </div>
      )}
      <Card grow data-testid="sweepstakes-entry-card">
        <div>
          {SweepstakesEntryCardHeaderProps?.showInsideHeaderOn && (
            <div className={headerClasses.inside} data-testid="sweepstakes-header-inside">
              <RenderHeader
                href={SweepstakesEntryCardHeaderProps.href}
                onClick={SweepstakesEntryCardHeaderProps.onClick}
                openNewWindow={SweepstakesEntryCardHeaderProps.openNewWindow}
              />
            </div>
          )}
          <div className="sui-flex sui-items-start sui-justify-between">
            <div className="sui-flex sui-items-start sui-gap-3 sui-flex-1">
              <RenderLogo
                logoUrl={displayData?.logo}
                logoFallback={logoFallback}
                imgRetryCounter={imgRetryCounter}
              />
              <div className="sui-flex-1">
                <div
                  className="sui-flex sui-flex-col sm:sui-flex-row sui-items-start
                sm:sui-items-center sui-gap-2 sui-mb-2"
                >
                  <RenderStatusBadge status={displayData?.status} />
                </div>
                <RenderTitle title={displayData?.title} />
                <RenderDescription description={displayData?.cardDescription} />
                {showSigninButton && <RenderSignInButton isAdmin={isAdmin} onClick={handleAuthClick} />}
                {showMobileGetStarted && showGetStarted && (
                  <div className="sui-block sm:sui-hidden">
                    <RenderGetStartedButton
                      onClick={handleDetailsClick}
                      label="Get Started"
                    />
                  </div>
                )}
              </div>
            </div>
            {isAuthenticated() && displayData && (
              <div
                className={[
                  'sui-self-start sm:sui-self-center',
                  showMobileGetStarted ? 'sui-hidden sm:sui-block' : '',
                ].join(' ').trim()}
              >
                <RenderDetailsButton onClick={handleDetailsClick} />
              </div>
            )}
          </div>
          {isAuthenticated() && hasSteps && (
            <RenderProgressSection sweepstakesData={displayData} />
          )}
        </div>
      </Card>
    </div>
  );
};

SweepstakesEntryCard.propTypes = {
  SweepstakesEntryCardHeaderProps: PropTypes.shape({
    href: PropTypes.string,
    onClick: PropTypes.func,
    openNewWindow: PropTypes.bool,
    showOutsideHeaderOn: PropTypes.oneOfType([
      PropTypes.oneOf(['MW', 'TW', 'DT']),
      PropTypes.arrayOf(PropTypes.oneOf(['MW', 'TW', 'DT'])),
    ]),
    showInsideHeaderOn: PropTypes.oneOfType([
      PropTypes.oneOf(['MW', 'TW', 'DT']),
      PropTypes.arrayOf(PropTypes.oneOf(['MW', 'TW', 'DT'])),
    ]),
  }),
};

SweepstakesEntryCard.defaultProps = {
  SweepstakesEntryCardHeaderProps: null,
};
